import { useState, useEffect, useMemo, useCallback } from "react";
import {
    useGetAuditLogsByTypeQuery,
    useLazyGetAuditLogsByTypeQuery
} from "../../../store/services/tenancy.service";
import {
    useCancelOnDemandScheduledContentMutation
} from "../../../store/services/ondemand-service";
import { useSelector } from "react-redux";
import Table from "../../../components/Table/Table";
import { Circle } from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, Snackbar, Typography } from "@mui/material";
import { formatDateTime } from "../../../utils/formatDateTime";
import ConfirmationDialog from "../../../utils/confirmationDialog";

const SCHEDULED_STATUS = 'Waiting to send the email';

export default function ScheduledContentTable() {
    const { tenancyId } = useSelector((state) => state.tenancy);
    const [pageSize] = useState(100);

    const {
        data: auditData,
        isLoading: auditLoading,
        error: auditError,
        isFetching,
        refetch
    } = useGetAuditLogsByTypeQuery({
        tenancyId,
        auditType: 'OnDemand',
        limit: pageSize,
        status: SCHEDULED_STATUS
    });

    const [loadMore, { isLoading: loadingMore }] = useLazyGetAuditLogsByTypeQuery();

    const [cancelOnDemandScheduledContent] = useCancelOnDemandScheduledContentMutation();
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogContent, setDialogContent] = useState("");
    const [dialogAction, setDialogAction] = useState(null);

    const [paginationModel, setPaginationModel] = useState({
        pageSize: pageSize,
        page: 0
    });

    useEffect(() => {
        refetch();
    }, [refreshTrigger, refetch]);

    const handleLoadMore = useCallback(async () => {
        if (auditData?.nextToken) {
            await loadMore({
                tenancyId,
                auditType: 'OnDemand',
                limit: pageSize,
                nextToken: auditData.nextToken,
                status: SCHEDULED_STATUS
            });
        }
    }, [auditData?.nextToken, loadMore, tenancyId, pageSize]);

    const handleCancelScheduledContent = (onDemandId) => {
        setDialogTitle("Cancel Scheduled Content");
        setDialogContent("Are you sure you want to cancel sending this content? This action is irreversible.");
        setDialogAction(() => () => {
            cancelOnDemandScheduledContent({ tenancyId, onDemandId })
                .unwrap()
                .then(() => {
                    setSnackbarMessage("Content successfully cancelled");
                    setSnackbarSeverity("success");
                    setSnackbarOpen(true);
                    setRefreshTrigger(prev => prev + 1);
                })
                .catch((error) => {
                    console.error("Error cancelling content", error);
                    setSnackbarMessage("Error cancelling content");
                    setSnackbarSeverity("error");
                    setSnackbarOpen(true);
                });
        });
        setDialogOpen(true);
    };

    const headCells = [
        { id: "sentTypeName", label: "Content", initialVisible: true },
        { id: "sentType", label: "Format", initialVisible: true },
        { id: "dateTime", label: "Date to send", initialVisible: true },
        { id: "createdAt", label: "Created on", initialVisible: true },
        { id: "recipients", label: "Recipients", initialVisible: true },
        { id: "status", label: "Status", initialVisible: true },
        {
            id: "status-label",
            label: " ",
            initialVisible: true,
            render: (item) =>
                item.status === "Waiting to send the email" ? (
                    <Circle sx={{ height: 8, width: 8 }} color="warning" />
                ) : item.status === "Completed sending emails" ? (
                    <Circle sx={{ height: 8, width: 8 }} color="primary" />
                ) : (
                    <Circle sx={{ height: 8, width: 8 }} color="error" />
                )
        },
        {
            id: "action",
            label: "Action",
            initialVisible: true,
            render: (item) => (
                <Button
                    variant="contained"
                    size="small"
                    color="error"
                    onClick={() => handleCancelScheduledContent(item.stepFunctionId)}
                >
                    Cancel
                </Button>
            )
        }
    ];

    const formattedAuditData = useMemo(() => {
        const items = auditData?.items || [];
        return items.map(item => ({
            ...item,
            dateTime: formatDateTime(item.waitingTime),
            createdAt: formatDateTime(item.dateTime),
            recipients: item.sentToName || item.sentTo
        }));
    }, [auditData]);

    const isLoading = auditLoading || (isFetching && !(auditData?.items?.length));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Table
                name={"scheduled on-demand sims and courses"}
                data={formattedAuditData}
                error={auditError}
                loading={isLoading}
                headCells={headCells}
                search={false}
                editColumn={false}
                checkbox={false}
                toolbarActions={null}
                showToolbarActions={false}
                selected={[]}
                setSelected={() => { }}
                maxTableHeight="calc(100vh - 300px)"
                initialVisibleColumns={headCells.filter(cell => cell.initialVisible).map(cell => cell.label)}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
            />

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 2,
                    py: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider'
                }}
            >
                {auditData?.hasMore && (
                    <Button
                        variant="outlined"
                        onClick={handleLoadMore}
                        disabled={loadingMore || isFetching}
                        startIcon={loadingMore ? <CircularProgress size={16} /> : null}
                    >
                        {loadingMore ? 'Loading...' : 'Load More'}
                    </Button>
                )}

                <Typography variant="body2" color="text.secondary">
                    Showing {formattedAuditData.length} records
                    {auditData?.hasMore && ' (more available)'}
                </Typography>
            </Box>

            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            <ConfirmationDialog
                open={dialogOpen}
                title={dialogTitle}
                content={dialogContent}
                onConfirm={() => {
                    setDialogOpen(false);
                    dialogAction();
                }}
                onCancel={() => setDialogOpen(false)}
            />
        </Box>
    );
}
